import React from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";
import { colorVariantClasses } from "@/theme/AppColorVariant";
import { ActionSelectItem } from "@/components/common/Table/File/ActionSelectItem";
import {
  ConflictRuleCatalog,
  ConflictRuleDefinition,
  isCasesOnly,
  OTHER_COPIES_ACTIONS,
  RuleCopyCount,
  WinnerKind,
  WinnerKindLabels,
} from "@/models/ConflictRule";
import { CONFLICT_FILE_FAMILIES } from "@/models/OrgConflictResolution";
import { MigrationType, MigrationTypeLabels } from "@/models/Statistics/MigrationType";
import { ConflictReason, ConflictReasonLabels } from "@/models/ConflictReason";
import {
  ConflictResolutionAction,
  ConflictResolutionActionColors,
  ConflictResolutionActionDescriptions,
  ConflictResolutionActionLabels,
} from "@/models/ConflictResolutionAction";
import { conditionRowPointer, RuleDiagnostics } from "@/lib/conflictRuleDiagnostics";
import RuleConditionList from "./RuleConditionList";
import RuleDiagnosticList from "./RuleDiagnosticList";
import RuleCaseListControl from "./RuleCaseListControl";

const REASONS: ConflictReason[] = [
  ConflictReason.PathCollision,
  ConflictReason.PathCollisionDuplicate,
  ConflictReason.SizeMismatch,
  ConflictReason.HashMismatch,
];

/** Fields the form shows findings under; any other finding is listed above the form. */
const STATIC_FIELDS = [
  "/appliesTo/families",
  "/appliesTo/reasons",
  "/appliesTo/caseNumbers",
  "/winner/kind",
  "/winner/when",
  "/otherCopies/action",
  "/otherCopies/onlyIf",
  "/checks/copyCount",
  "/checks/sameSize",
  "/checks/differentFolders",
  "/options/allowReplacingCopiesInSharePoint",
  "/options/allowAcceptingDifferentContent",
];

/** Every field the form currently shows findings under, condition rows included. */
export const renderedRuleFields = (rule: ConflictRuleDefinition): string[] => [
  ...STATIC_FIELDS,
  ...(rule.winner.kind === WinnerKind.MatchingCopy
    ? rule.winner.when.map((_, index) => conditionRowPointer("/winner/when", index))
    : []),
  ...rule.otherCopies.onlyIf.map((_, index) => conditionRowPointer("/otherCopies/onlyIf", index)),
];

const labelOf = (catalog: ConflictRuleCatalog, list: "checks" | "options", key: string, fallback: string): string =>
  catalog[list].find((entry) => entry.key === key)?.label ?? fallback;

const Section: React.FC<{ title: string; description?: string; children: React.ReactNode }> = ({
  title,
  description,
  children,
}) => (
  <section className="flex flex-col gap-3 rounded-md border bg-card p-4">
    <div>
      <h3 className="text-sm font-semibold">{title}</h3>
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
    </div>
    {children}
  </section>
);

interface ConflictRuleFormProps {
  rule: ConflictRuleDefinition;
  onChange: React.Dispatch<React.SetStateAction<ConflictRuleDefinition>>;
  catalog: ConflictRuleCatalog;
  diagnostics: RuleDiagnostics;
  disabled?: boolean;
}

/**
 * The rule form: what the rule applies to, which copy wins, what the other copies get, the checks a group must pass
 * and the options that allow decisions the rule otherwise skips. It edits exactly the rule's JSON, so a saved,
 * imported or past run's rule fills it whole. Remount it (a new `key`) when the rule is replaced from outside: the
 * case list's add box keeps a half-typed entry of its own.
 */
const ConflictRuleForm: React.FC<ConflictRuleFormProps> = ({ rule, onChange, catalog, diagnostics, disabled }) => {
  const casesOnly = isCasesOnly(rule);

  const setScope = (change: Partial<ConflictRuleDefinition["appliesTo"]>) =>
    onChange({ ...rule, appliesTo: { ...rule.appliesTo, ...change } });
  const setChecks = (change: Partial<ConflictRuleDefinition["checks"]>) =>
    onChange({ ...rule, checks: { ...rule.checks, ...change } });
  const setOptions = (change: Partial<ConflictRuleDefinition["options"]>) =>
    onChange({ ...rule, options: { ...rule.options, ...change } });

  const copyCount = rule.checks.copyCount;
  // The mode is the key the count is stored under, even while its number is cleared ({ atLeast: null }): reading it
  // from the number alone would flip a cleared "At least" to "Exactly" and store the next digit under the wrong key.
  const copyCountMode: keyof RuleCopyCount =
    copyCount?.atLeast != null || (copyCount?.exactly == null && copyCount?.atLeast !== undefined)
      ? "atLeast"
      : "exactly";
  const copyCountValue = copyCount ? (copyCount[copyCountMode] ?? null) : null;
  const setCopyCount = (mode: keyof RuleCopyCount, value: number | null) =>
    setChecks({ copyCount: mode === "exactly" ? { exactly: value } : { atLeast: value } });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="conflict-rule-name">Rule name</Label>
        <Input
          id="conflict-rule-name"
          value={rule.name ?? ""}
          onChange={(event) => onChange({ ...rule, name: event.target.value })}
          placeholder="Names the rule in the library and in the audit log"
          disabled={disabled}
        />
      </div>

      <Section title="Applies to" description="The groups the rule looks at.">
        <div className="flex flex-col gap-1.5">
          <Label id="conflict-rule-families-label" className="text-xs text-muted-foreground">
            Families
          </Label>
          <ToggleGroup
            type="multiple"
            aria-labelledby="conflict-rule-families-label"
            variant="outline"
            size="sm"
            value={rule.appliesTo.families}
            onValueChange={(families) => setScope({ families: families as MigrationType[] })}
            disabled={disabled}
            className="flex-wrap justify-start"
          >
            {CONFLICT_FILE_FAMILIES.map((family) => (
              <ToggleGroupItem key={family} value={family}>
                {MigrationTypeLabels[family]}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <RuleDiagnosticList diagnostics={diagnostics.at("/appliesTo/families")} />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label id="conflict-rule-reasons-label" className="text-xs text-muted-foreground">
            Conflict types
          </Label>
          <ToggleGroup
            type="multiple"
            aria-labelledby="conflict-rule-reasons-label"
            variant="outline"
            size="sm"
            value={rule.appliesTo.reasons}
            onValueChange={(reasons) => setScope({ reasons: reasons as ConflictReason[] })}
            disabled={disabled}
            className="flex-wrap justify-start"
          >
            {REASONS.map((reason) => (
              <ToggleGroupItem key={reason} value={reason}>
                {ConflictReasonLabels[reason]}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <RuleDiagnosticList diagnostics={diagnostics.at("/appliesTo/reasons")} />
        </div>

        <div className="flex flex-col gap-1.5">
          {casesOnly || rule.appliesTo.caseNumbers.length > 0 ? (
            // A list left on a rule that no longer covers cases alone stays editable, so the error it raises can be
            // cleared from here.
            <RuleCaseListControl
              label="Only these cases (optional, cases only)"
              values={rule.appliesTo.caseNumbers}
              onChange={(update) =>
                onChange((current) => ({
                  ...current,
                  appliesTo: { ...current.appliesTo, caseNumbers: update(current.appliesTo.caseNumbers) },
                }))
              }
              reasons={rule.appliesTo.reasons}
              disabled={disabled}
            />
          ) : (
            <>
              <Label className="text-xs text-muted-foreground">Only these cases (optional, cases only)</Label>
              <p className="text-xs text-muted-foreground">Only a rule on Cases alone can list cases.</p>
            </>
          )}
          <p className="text-xs text-muted-foreground">
            {rule.appliesTo.caseNumbers.length === 0
              ? "Every case."
              : "Matched ignoring letter case. A number on its own, such as 201, also finds TW-201."}
          </p>
          <RuleDiagnosticList diagnostics={diagnostics.at("/appliesTo/caseNumbers")} />
        </div>
      </Section>

      <Section title="Winner" description="The copy that keeps the name.">
        <ToggleGroup
          type="single"
          aria-label="Winner"
          variant="outline"
          size="sm"
          value={rule.winner.kind}
          onValueChange={(kind) => {
            if (kind) onChange({ ...rule, winner: { ...rule.winner, kind: kind as WinnerKind } });
          }}
          disabled={disabled}
          className="flex-wrap justify-start"
        >
          {Object.values(WinnerKind).map((kind) => (
            <ToggleGroupItem key={kind} value={kind}>
              {WinnerKindLabels[kind]}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <p className="text-xs text-muted-foreground">
          {rule.winner.kind === WinnerKind.MatchingCopy
            ? "The one flagged copy, or copy already in SharePoint, that matches every condition."
            : rule.winner.kind === WinnerKind.InSharePoint
              ? "The copy NFS already uploaded to the path."
              : "No copy wins: every flagged copy gets the other copies' action, and which one ends up with the plain name is not predictable."}
        </p>
        {/* Fixed, not chosen: only Replace from disk guarantees the winner the plain name, and only it holds the
            renamed and accepting copies until it lands and gets the group checks that protect a winner. */}
        {rule.winner.kind !== WinnerKind.None && (
          <p className="flex flex-wrap items-baseline gap-x-1.5 text-sm">
            <span className="text-muted-foreground">The winner gets</span>
            {rule.winner.kind === WinnerKind.MatchingCopy ? (
              <>
                <span
                  className={cn(
                    "font-medium",
                    colorVariantClasses[ConflictResolutionActionColors[ConflictResolutionAction.ReplaceFromDisk]]?.text
                  )}
                >
                  {ConflictResolutionActionLabels[ConflictResolutionAction.ReplaceFromDisk]}
                </span>
                <span className="text-xs text-muted-foreground">
                  (a winner already in SharePoint keeps the name and uploads nothing)
                </span>
              </>
            ) : (
              <span className="font-medium">no action: it keeps the name</span>
            )}
          </p>
        )}
        <RuleDiagnosticList diagnostics={diagnostics.at("/winner/kind")} />
        {rule.winner.kind === WinnerKind.MatchingCopy && (
          <div role="group" aria-label="Winner conditions" className="flex flex-col gap-1.5">
            <RuleConditionList
              section="/winner/when"
              conditions={rule.winner.when}
              onChange={(when) => onChange({ ...rule, winner: { ...rule.winner, when } })}
              catalog={catalog}
              diagnostics={diagnostics}
              casesOnly={casesOnly}
              addLabel="Add winner condition"
              disabled={disabled}
            />
            <RuleDiagnosticList diagnostics={diagnostics.at("/winner/when")} />
          </div>
        )}
      </Section>

      <Section title="Other copies" description="Every flagged copy that is not the winner.">
        <div className="flex flex-wrap items-center gap-2">
          <Label htmlFor="conflict-rule-other-copies-action" className="text-sm font-normal text-muted-foreground">
            Other copies get
          </Label>
          <Select
            value={rule.otherCopies.action}
            onValueChange={(action) =>
              onChange({ ...rule, otherCopies: { ...rule.otherCopies, action: action as ConflictResolutionAction } })
            }
            disabled={disabled}
          >
            <SelectTrigger
              id="conflict-rule-other-copies-action"
              size="sm"
              className="w-64"
              aria-invalid={diagnostics.hasErrorAt("/otherCopies/action")}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OTHER_COPIES_ACTIONS.map((action) => (
                <ActionSelectItem
                  key={action}
                  value={action}
                  label={ConflictResolutionActionLabels[action]}
                  description={ConflictResolutionActionDescriptions[action]}
                  labelClassName={colorVariantClasses[ConflictResolutionActionColors[action]]?.text}
                />
              ))}
            </SelectContent>
          </Select>
        </div>
        <RuleDiagnosticList diagnostics={diagnostics.at("/otherCopies/action")} />
        <div role="group" aria-labelledby="conflict-rule-only-if-label" className="flex flex-col gap-1.5">
          <Label id="conflict-rule-only-if-label" className="text-xs text-muted-foreground">
            Only if every other flagged copy matches (optional)
          </Label>
          <RuleConditionList
            section="/otherCopies/onlyIf"
            conditions={rule.otherCopies.onlyIf}
            onChange={(onlyIf) => onChange({ ...rule, otherCopies: { ...rule.otherCopies, onlyIf } })}
            catalog={catalog}
            diagnostics={diagnostics}
            casesOnly={casesOnly}
            addLabel="Add condition"
            disabled={disabled}
          />
          <RuleDiagnosticList diagnostics={diagnostics.at("/otherCopies/onlyIf")} />
        </div>
      </Section>

      <Section title="Safety checks" description="A group that fails one is skipped and listed with the reason.">
        <div className="flex flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <Checkbox
              id="conflict-rule-copy-count"
              checked={copyCount != null}
              onCheckedChange={(checked) => setChecks({ copyCount: checked === true ? { exactly: 2 } : null })}
              disabled={disabled}
            />
            <Label htmlFor="conflict-rule-copy-count" className="text-sm font-normal">
              {labelOf(catalog, "checks", "copyCount", "Exactly or at least N copies")}
            </Label>
            {copyCount != null && (
              <>
                <ToggleGroup
                  type="single"
                  aria-label="Exactly or at least"
                  variant="outline"
                  size="sm"
                  value={copyCountMode}
                  onValueChange={(mode) => {
                    if (mode) setCopyCount(mode as keyof RuleCopyCount, copyCountValue);
                  }}
                  disabled={disabled}
                >
                  <ToggleGroupItem value="exactly">Exactly</ToggleGroupItem>
                  <ToggleGroupItem value="atLeast">At least</ToggleGroupItem>
                </ToggleGroup>
                <Input
                  type="number"
                  min={2}
                  value={copyCountValue ?? ""}
                  onChange={(event) => {
                    const parsed = Number.parseInt(event.target.value, 10);
                    setCopyCount(copyCountMode, Number.isNaN(parsed) ? null : parsed);
                  }}
                  className="h-8 w-20"
                  disabled={disabled}
                  aria-label="Number of copies"
                  aria-invalid={diagnostics.at("/checks/copyCount").some((d) => d.severity === "error")}
                />
                <span className="text-xs text-muted-foreground">copies</span>
              </>
            )}
          </div>
          <RuleDiagnosticList diagnostics={diagnostics.at("/checks/copyCount")} />
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <Checkbox
              id="conflict-rule-same-size"
              checked={rule.checks.sameSize}
              onCheckedChange={(checked) => setChecks({ sameSize: checked === true })}
              disabled={disabled}
            />
            <Label htmlFor="conflict-rule-same-size" className="text-sm font-normal">
              {labelOf(catalog, "checks", "sameSize", "All the same size")}
            </Label>
          </div>
          <RuleDiagnosticList diagnostics={diagnostics.at("/checks/sameSize")} />
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <Checkbox
              id="conflict-rule-different-folders"
              checked={rule.checks.differentFolders != null}
              onCheckedChange={(checked) => setChecks({ differentFolders: checked === true ? "" : null })}
              disabled={disabled}
            />
            <Label htmlFor="conflict-rule-different-folders" className="text-sm font-normal">
              {labelOf(catalog, "checks", "differentFolders", "Copies in different \"<folder>\" folders")}
            </Label>
            {rule.checks.differentFolders != null && (
              <Input
                value={rule.checks.differentFolders}
                onChange={(event) => setChecks({ differentFolders: event.target.value })}
                placeholder="Folder name"
                className="h-8 w-40"
                disabled={disabled}
                aria-label="Folder name"
                aria-invalid={diagnostics.hasErrorAt("/checks/differentFolders")}
              />
            )}
          </div>
          <RuleDiagnosticList diagnostics={diagnostics.at("/checks/differentFolders")} />
        </div>
      </Section>

      <Section
        title="Options"
        description="Decisions the rule otherwise skips because they lose content. The preview counts the copies each one affects."
      >
        {(
          [
            ["allowReplacingCopiesInSharePoint", "Allow replacing copies already in SharePoint"],
            ["allowAcceptingDifferentContent", "Allow accepting copies whose content may differ"],
          ] as const
        ).map(([key, fallback]) => (
          <div key={key} className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <Checkbox
                id={`conflict-rule-${key}`}
                checked={rule.options[key]}
                onCheckedChange={(checked) => setOptions({ [key]: checked === true })}
                disabled={disabled}
              />
              <Label
                htmlFor={`conflict-rule-${key}`}
                className={cn("text-sm font-normal", rule.options[key] && "text-amber-700 dark:text-amber-400")}
              >
                {labelOf(catalog, "options", key, fallback)}
              </Label>
            </div>
            <RuleDiagnosticList diagnostics={diagnostics.at(`/options/${key}`)} />
          </div>
        ))}
      </Section>
    </div>
  );
};

export default ConflictRuleForm;
